import { useEffect, useState, useRef } from 'react';
import { ModalContentVideo, ModalTriggerVideo, ModalVideo } from '../ui/ModalVideo/ModalVideo';
import YouTube, { type YouTubePlayer } from 'react-youtube';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Button } from '../ui/Button';
import { Play, Pause, Volume2, VolumeX, X, ArrowRight } from 'lucide-react';
import * as Modal from '@radix-ui/react-dialog';
import { useTranslation } from '../lib/i18n/TranslationContext';

interface Snippet {
	title: string;
	thumbnails: {
		default: {
			url: string;
		};
		medium: {
			url: string;
		};
		high: {
			url: string;
		};
		maxres: {
			url: string;
		};
	};
	resourceId: {
		videoId: string;
	};
}

interface Video {
	id: string;
	etag: string;
	snippet: Snippet;
}
const StorieContainer = ({ video }: { video: Video }) => {
	const playerRef = useRef<YouTubePlayer | null>(null);
	const [isPlaying, setIsPlaying] = useState(true);
	const [isMuted, setIsMuted] = useState(false);

	if (!video?.snippet?.thumbnails?.default) return <></>;

	if (video.snippet.title === 'Private video') return;

	const opts = {
		height: '900px',
		width: '370px',
		playerVars: {
			autoplay: 1,
			controls: 0,
			modestbranding: 1,
			rel: 0,
			showinfo: 0,
		},
	};

	const handleReady = (event: { target: YouTubePlayer }) => {
		playerRef.current = event.target;
		playerRef.current.unMute();
	};

	const togglePlay = () => {
		if (!playerRef.current) return;
		const playerState = playerRef.current.getPlayerState();

		const isCurrentlyPlaying = playerState === 1;

		if (isCurrentlyPlaying) {
			playerRef.current?.pauseVideo();
			setIsPlaying(false);
		} else {
			playerRef.current?.playVideo();
			setIsPlaying(true);
		}
	};
	const toggleMute = () => {
		if (!playerRef.current) return;
		if (isMuted) {
			playerRef.current.unMute();
		} else {
			playerRef.current.mute();
		}
		setIsMuted(!isMuted);
	};

	return (
		<ModalVideo>
			<ModalTriggerVideo className='select-none' draggable={false}>
				<div className='size-full flex items-center justify-center'>
					<div className='size-[calc(100%-2px)] flex items-center justify-center rounded-full overflow-hidden border-[2px] border-white'>
						<img draggable={false} className='size-full object-cover' src={video.snippet.thumbnails?.maxres?.url} alt={video.snippet.title} />
					</div>
				</div>
			</ModalTriggerVideo>

			<ModalContentVideo className='w-auto h-[710px] !px-2'>
				<div className='relative h-[650px] overflow-hidden rounded-xl bg-black'>
					<div className='absolute w-full flex justify-between items-center h-12 top-4 px-4 z-10'>
						<div className='flex flex-row gap-2'>
							<Button onClick={togglePlay} className='backdrop-blur-[20px] size-12 text-white bg-white/5 hover:bg-white/15 p-0 rounded-full flex items-center justify-center'>
								{isPlaying ? <Pause className='size-6' /> : <Play className='size-6' />}
							</Button>
							<Button onClick={toggleMute} className='backdrop-blur-[20px] size-12 text-white bg-white/5 hover:bg-white/15 p-0 rounded-full flex items-center justify-center'>
								{isMuted ? <VolumeX className='size-6' /> : <Volume2 className='size-6' />}
							</Button>
						</div>

						<Modal.Close>
							<Button className='backdrop-blur-[20px] size-12 text-white bg-white/5 hover:bg-white/15 p-0 rounded-full flex items-center justify-center'>
								<X className='size-6' />
							</Button>
						</Modal.Close>
					</div>

					<div className='absolute w-full flex justify-between items-center bottom-0 h-[80px] px-4 z-10 rounded-b-[12px] overflow-hidden [background-image:linear-gradient(0deg,_rgba(0,0,0,0.80),_transparent)]'>
						<a href='https://www.youtube.com/@BrunoBritoo/shorts' className='text-sm text-white/80' target='_blank'>
							<span
								dangerouslySetInnerHTML={{
									__html: video.snippet.title.replace(/(#\w+)/g, '<strong>$1</strong>'),
								}}
							/>
						</a>
					</div>

					<YouTube
						onPlay={() => {
							setIsPlaying(true);
							if (!isMuted && playerRef.current) {
								playerRef.current.unMute();
							}
						}}
						onPause={() => setIsPlaying(false)}
						opts={opts}
						onReady={handleReady}
						className='w-[370px] h-[715px] overflow-hidden flex items-center justify-center'
						iframeClassName='relative -top-[37px]'
						videoId={video.snippet.resourceId.videoId}
					/>
				</div>
			</ModalContentVideo>
		</ModalVideo>
	);
};
interface DinamicParams {
	search: string;
	playlistItems: string;
}

const EmpreenderStories = () => {
	const [videos, setVideos] = useState([] as Video[]);
	const { t } = useTranslation();

	useEffect(() => {
		const dinamicParams = {
			search: `&channelId=UCEvuz8QT_dk3O5G7Ee865AQ`,
			playlistItems: `&playlistId=PLaGX332bqyauM43qwd-LsQJ1zrq2Vmp7m`,
		} as DinamicParams;

		const URL = `https://app.empreender.plus/buscarVideosYoutube.php?${dinamicParams.playlistItems}&order=date&part=snippet&maxResults=${50}`;

		fetch(URL)
			.then((response) => {
				return response.json();
			})
			.then((data) => {
				setVideos(data.items);
			});
	}, []);

	const filteredVideos = videos
		.filter(
			(video) =>
				video?.snippet?.title !== 'Private video' &&
				video?.snippet?.resourceId?.videoId
		)
		.slice(0, -1)
		.reverse();

	return (
		<div className='[container-type:inline-size] flex flex-row flex-wrap items-center justify-center gap-4 max-w-5xl w-full p-0'>
			<div className='w-[96%] [@container_(min-width:900px)]:p-6'>
				<div className='flex flex-row w-full items-center'>
					<h1 className='w-full text-gray-900 text-lg not-italic font-semibold leading-7'>{t('Novidades na Empreender')}</h1>
					<a href='https://app.empreender.plus/' target='_blank' className='flex-shrink-0 text-xs flex flex-row items-center gap-1 underline underline-offset-1'>
						{t('Saiba mais')} <ArrowRight className='size-3' />
					</a>
				</div>

				<div className='w-full flex flex-row items-center flex-wrap justify-center gap-4 mt-6'>
					<Swiper
						className="!h-auto"
						slidesPerView="auto"
						spaceBetween={16}
						slidesPerGroup={3}
					>
						{filteredVideos.map((video) => (
							<SwiperSlide
								key={video.snippet.resourceId.videoId}
								className="!size-[66px] flex items-center cursor-pointer justify-center bg-gradient-to-tl from-[#8346F9] via-gray-900 to-[#8346F9] rounded-full overflow-hidden"
							>
								<StorieContainer video={video} />
							</SwiperSlide>
						))}
					</Swiper>
				</div>
			</div>
		</div>
	);
};

export default EmpreenderStories;
